/**
 * Small UI hooks shared across game views. These deliberately live in
 * `lib/` (not under a specific game) — each game can pull them in without
 * coupling to each other.
 */

import { useEffect, useRef, useState } from "react";

/** Tracks the current viewport width, rAF-throttled to avoid resize storms. */
export function useWindowWidth(): number {
  const [w, setW] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerWidth : 1280
  );
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setW(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return w;
}

/**
 * Fires `fn` whenever `key` changes from the previous render. Useful for
 * clearing selection state when a phase or trick transitions.
 */
export function useResetOnChange(key: string, fn: () => void): void {
  const ref = useRef(key);
  useEffect(() => {
    if (ref.current !== key) {
      ref.current = key;
      fn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * Linear interpolation of a card width between two viewport breakpoints.
 * Below `minVw` returns `min`; above `maxVw` returns `max`; in between
 * scales proportionally. Used to size on-table cards responsively.
 */
export function lerpCardWidth(
  vw: number,
  min: number,
  max: number,
  minVw = 1024,
  maxVw = 1920
): number {
  if (vw <= minVw) return min;
  if (vw >= maxVw) return max;
  const t = (vw - minVw) / (maxVw - minVw);
  return Math.round(min + (max - min) * t);
}


/**
 * Stable ref for the `.game-view` element. Historically this hook also
 * reflowed the table when a score-bar opened/closed; we removed that
 * behavior because the board would expand on collapse but not re-shrink
 * on reopen (the bar component unmounts/remounts its `.score-bar`
 * element internally, which our ResizeObserver couldn't pick up without
 * a parent re-render).
 *
 * Instead, score panels on small viewports now default to collapsed
 * (per game). When a user expands one, it overlaps the top of the
 * board until they collapse it again — they can decide which they
 * want at any time, but the board layout never moves under them.
 *
 * The exported ref is kept so call-sites don't have to change.
 */
export function useDynamicTableTop(): (el: HTMLDivElement | null) => void {
  const ref = useRef<HTMLDivElement | null>(null);
  return (el: HTMLDivElement | null) => {
    ref.current = el;
  };
}

/**
 * Browser fullscreen toggle. Tracks the document's fullscreen state via
 * the `fullscreenchange` event so the UI stays in sync with the user
 * pressing Esc or using a system gesture to exit.
 *
 * `supported` is `false` on iOS Safari/Chrome where the Fullscreen API
 * is restricted to `<video>` elements — UI should hide the toggle in
 * that case (and suggest "Add to Home Screen" as the iOS workaround).
 */
export function useFullscreen(): {
  supported: boolean;
  active: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
} {
  const supported =
    typeof document !== "undefined" &&
    typeof document.documentElement?.requestFullscreen === "function";
  const [active, setActive] = useState<boolean>(() =>
    typeof document !== "undefined" && document.fullscreenElement !== null
  );
  useEffect(() => {
    if (!supported) return undefined;
    const onChange = () => setActive(document.fullscreenElement !== null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [supported]);
  function enter() {
    if (!supported) return;
    // Some browsers (older Safari) reject if not user-initiated —
    // swallow so we don't break the page.
    void document.documentElement.requestFullscreen().catch(() => {});
  }
  function exit() {
    if (!supported) return;
    if (document.fullscreenElement === null) return;
    void document.exitFullscreen().catch(() => {});
  }
  function toggle() {
    if (document.fullscreenElement === null) enter();
    else exit();
  }
  return { supported, active, enter, exit, toggle };
}
